import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Widget from './Widget';
import { fetchAccounts } from '../../api/api';

/**
 * AccountsWidget - Displays trading accounts overview
 * 
 * Props:
 * - hideTitle: boolean - hide widget title
 * - onAccountSelect: function - callback when account is selected
 * - compact: boolean - use compact view
 */
const AccountsWidget = ({ 
  hideTitle = false,
  onAccountSelect = null,
  compact = false
}) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAccounts();
      const accountsArray = Array.isArray(data) ? data : (data?.results || []);
      setAccounts(accountsArray);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const formatCurrency = (value, currency = 'USD') => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-500';
    const s = status.toLowerCase();
    if (s === 'connected' || s === 'active') return 'bg-success';
    if (s === 'disconnected' || s === 'inactive') return 'bg-destructive';
    if (s === 'pending' || s === 'connecting') return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  const getPlatformIcon = (platform) => {
    if (!platform) return '📊';
    const p = platform.toLowerCase();
    if (p === 'mt5' || p === 'metatrader5') return '📈';
    if (p === 'ctrader') return '📉';
    if (p === 'interactivebrokers' || p === 'ib') return '🏦';
    return '📊';
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + (parseFloat(acc.balance) || 0), 0);
  const totalEquity = accounts.reduce((sum, acc) => sum + (parseFloat(acc.equity) || 0), 0);

  return (
    <Widget title="Accounts" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {/* Summary header */}
        <div className="px-2 sm:px-3 py-2 border-b border-borderDefault bg-secondary/5">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs text-textSecondary">Total Balance</div>
              <div className="text-sm sm:text-lg font-semibold text-textPrimary truncate">
                {formatCurrency(totalBalance)}
              </div>
            </div>
            <div className="text-right min-w-0">
              <div className="text-[10px] sm:text-xs text-textSecondary">Total Equity</div>
              <div className="text-sm sm:text-lg font-semibold text-textPrimary truncate">
                {formatCurrency(totalEquity)}
              </div>
            </div>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] sm:text-xs text-textSecondary">
              {accounts.length} account{accounts.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={loadAccounts}
              disabled={loading}
              className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="p-4 text-center text-destructive text-sm">{error}</div>
        )}

        {!error && accounts.length === 0 && !loading && (
          <div className="p-4 text-center text-textSecondary text-sm">No accounts found</div>
        )}

        {/* Accounts list */}
        <div className="flex-1 overflow-y-auto">
          {accounts.map((account, idx) => (
            <div
              key={account.id || idx}
              className={`p-2 sm:p-3 border-b border-borderDefault hover:bg-secondary/10 transition-colors ${
                onAccountSelect ? 'cursor-pointer' : ''
              }`}
              onClick={() => onAccountSelect?.(account)}
            >
              <div className="flex items-start justify-between gap-1 sm:gap-2">
                {/* Account info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-base sm:text-lg">{getPlatformIcon(account.platform)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-xs sm:text-sm text-textPrimary truncate">
                        {account.name || account.account_name || `Account ${account.account_number}`}
                      </div>
                      <div className="text-[10px] sm:text-xs text-textSecondary flex items-center gap-1 sm:gap-2">
                        <span>{account.platform}</span>
                        {account.account_number && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <span className="hidden sm:inline">#{account.account_number}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status and balance */}
                <div className="text-right flex-shrink-0">
                  <div className="flex items-center justify-end gap-1 mb-0.5 sm:mb-1">
                    <span className={`w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full ${getStatusColor(account.status || account.connection_status)}`}></span>
                    <span className="text-[10px] sm:text-xs text-textSecondary capitalize hidden sm:inline">
                      {account.status || account.connection_status || 'Unknown'}
                    </span>
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-textPrimary">
                    {formatCurrency(account.balance, account.currency)}
                  </div>
                </div>
              </div>

              {/* Extended info (non-compact view) */}
              {!compact && (
                <div className="mt-1.5 sm:mt-2 grid grid-cols-3 gap-1 sm:gap-2 text-[10px] sm:text-xs">
                  <div>
                    <span className="text-textSecondary">Eq: </span>
                    <span className="text-textPrimary">{formatCurrency(account.equity, account.currency)}</span>
                  </div>
                  <div>
                    <span className="text-textSecondary">Mgn: </span>
                    <span className="text-textPrimary">{formatCurrency(account.margin || account.used_margin, account.currency)}</span>
                  </div>
                  <div>
                    <span className="text-textSecondary">Free: </span>
                    <span className="text-textPrimary">{formatCurrency(account.free_margin, account.currency)}</span>
                  </div>
                </div>
              )}

              {/* Profit/Loss indicator */}
              {account.profit !== undefined && (
                <div className="mt-2">
                  <span className={`text-xs font-medium ${
                    parseFloat(account.profit) >= 0 ? 'text-success' : 'text-destructive'
                  }`}>
                    P/L: {parseFloat(account.profit) >= 0 ? '+' : ''}{formatCurrency(account.profit, account.currency)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Widget>
  );
};

AccountsWidget.propTypes = {
  hideTitle: PropTypes.bool,
  onAccountSelect: PropTypes.func,
  compact: PropTypes.bool,
};

export default AccountsWidget;
